
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      toast.success("Logged in successfully!");
      navigate("/");
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // If still checking auth state, show loading
  if (loading && !email && !password) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // Don't show the login form if we're already authenticated
  if (user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-primary via-white to-brand-secondary/30 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white/90 backdrop-blur-sm rounded-lg p-8 shadow-lg border border-gray-100 mb-6">
        <div className="flex justify-center mb-6">
          <img 
            src="/lovable-uploads/13c2dbae-e3b7-411e-9098-8c42e9de92dd.png" 
            alt="Logo" 
            className="h-12" 
          />
        </div>
        
        <h1 className="text-2xl font-semibold mb-6 text-center text-brand-dark">
          Log In
        </h1>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="border-gray-300 focus:border-brand-secondary focus:ring-brand-secondary"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="border-gray-300 focus:border-brand-secondary focus:ring-brand-secondary"
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white"
            disabled={loading}
          >
            {loading ? "Processing..." : "Log In"}
          </Button>
        </form>
        
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600 mb-2">
            Need an account?
          </p>
          <a 
            href="https://portal.venturecatalysts.co.uk/" 
            className="inline-block text-sm font-medium text-brand-primary hover:text-brand-primary/80 hover:underline"
            target="_blank" 
            rel="noopener noreferrer"
          >
            Sign up at the Venture Catalysts Portal
          </a>
        </div>
      </div>
      
      <div className="text-center text-sm text-brand-dark/70">
        <p>Market Size Calculator © 2025</p>
      </div>
    </div>
  );
}
